import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { serviceRequestService } from '../../services/service-request.service';
import { ServiceRequest } from '../../types';
import { Select } from '../../components/ui/Select';
import { Loading } from '../../components/ui/Loading';

export const RequestsPage = () => {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadRequests();

    const interval = setInterval(() => {
      loadRequests(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [statusFilter]);

  const loadRequests = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const result = await serviceRequestService.getMemberRequests({
        status: statusFilter || undefined
      });
      setRequests(result.items);
    } catch (error: any) {
      if (!silent) toast.error(error.message || 'Failed to load requests');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Service Requests</h1>
        <p className="text-gray-600">View and track your service requests</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <Select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'All Status' },
            { value: 'PENDING', label: 'Pending' },
            { value: 'IN_PROGRESS', label: 'In Progress' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'CANCELLED', label: 'Cancelled' }
          ]}
        />
      </div>

      <div className="space-y-4">
        {requests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">No service requests found</p>
          </div>
        ) : (
          requests.map((request) => (
            <div key={request.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{request.type}</h3>
                  <p className="text-sm text-gray-500">Requested on {formatDate(request.createdAt)}</p>
                </div>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(request.status)}`}>
                    {request.status}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(request.priority)}`}>
                    {request.priority}
                  </span>
                </div>
              </div>
              <p className="text-gray-700 mb-4">{request.details}</p>
              {request.notes && (
                <div className="bg-blue-50 rounded p-3">
                  <p className="text-sm text-blue-900">
                    <strong>Admin Notes:</strong> {request.notes}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
